"use client";

import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Plug2, Radio, Bot, Code2 } from "lucide-react";
import type { Source, SourceKind } from "@/lib/types/routing";

const KIND_LABELS: Record<SourceKind, string> = {
  TradingView: "TradingView",
  TelegramBot: "Telegram",
  RestApi: "REST API",
  Manual: "Manual",
};

const KIND_ICONS: Record<SourceKind, typeof Plug2> = {
  TradingView: Radio,
  TelegramBot: Bot,
  RestApi: Code2,
  Manual: Plug2,
};

export interface SourceNodeData {
  source: Source;
  onNodeClick?: (source: Source) => void;
}

function SourceNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as SourceNodeData;
  const { source, onNodeClick } = nodeData;
  const Icon = KIND_ICONS[source.kind] ?? Plug2;
  const label = KIND_LABELS[source.kind] ?? source.kind;

  return (
    <div
      className="group flex min-w-[180px] cursor-pointer flex-col border border-border bg-background px-3 py-2.5 shadow-sm transition-colors hover:border-primary/60 hover:bg-muted/30"
      onClick={() => onNodeClick?.(source)}
    >
      {/* kind badge */}
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        {/* active dot */}
        <span
          className={
            "ms-auto h-1.5 w-1.5 rounded-full " +
            (source.isActive ? "bg-profit" : "bg-loss")
          }
        />
      </div>
      {/* name */}
      <p className="truncate text-sm font-medium leading-tight">{source.name}</p>

      {/* right-side output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        style={{
          width: 10,
          height: 10,
          background: "hsl(var(--primary))",
          border: "2px solid hsl(var(--background))",
          borderRadius: 0,
          right: -6,
        }}
      />
    </div>
  );
}

export const SourceNode = memo(SourceNodeComponent);
